import { BaseClient, type Paged } from '../base-client';
import type {
  CreateOccurrenceInput,
  CustomerVariableExpenseOccurrence,
  ImportOccurrencesResult,
  OccurrenceListQuery,
  PatchOccurrenceInput,
  VariableExpenseBulkEntry,
  VariableExpenseBulkResult,
  VariableExpenseGridMeta,
  VariableExpenseGridQuery,
  VariableExpenseGridRow,
} from './types';

type One = { data: CustomerVariableExpenseOccurrence };
type ImportResponse = { data: ImportOccurrencesResult };

const BY_ID_BASE = '/api/customer-variable-expense-occurrences';

const forLine = (customerExpenseId: string) =>
  `/api/customer-expenses/${encodeURIComponent(customerExpenseId)}/occurrences`;

function gridQuery(q: VariableExpenseGridQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {
    periodStart: q.periodStart,
    periodEnd: q.periodEnd,
  };
  if (q.customerId) out.customerId = q.customerId;
  if (q.expenseCatalogId) out.expenseCatalogId = q.expenseCatalogId;
  if (q.state) out.state = q.state;
  if (q.limit != null) out.limit = q.limit;
  if (q.offset != null) out.offset = q.offset;
  return out;
}

export class CustomerVariableExpenseOccurrencesClient extends BaseClient {
  /**
   * The month grid: every variable expense line in force for the period, each
   * with the amounts recorded inside it.
   *
   * Paged by LINE, not by occurrence — the unit of the screen is "a thing that
   * needs an amount", so a line with three fees and a line with none each
   * count as one row.
   */
  grid(query: VariableExpenseGridQuery): Promise<{
    data: VariableExpenseGridRow[];
    meta: VariableExpenseGridMeta;
  }> {
    return this.request(`${BY_ID_BASE}/grid`, { query: gridQuery(query) });
  }

  /**
   * Record amounts against many lines in one request.
   *
   * What the grid's single Save sends. Forty amounts cost one round trip, and
   * — more to the point — one decision by the user rather than forty chances
   * to leave the month half done. The result reports each row, so a line that
   * failed stays on screen instead of taking the batch with it.
   */
  bulkCreate(
    entries: readonly VariableExpenseBulkEntry[],
    opts: { idempotencyKey?: string } = {},
  ): Promise<{
    data: VariableExpenseBulkResult;
  }> {
    // Pass the same key when retrying the same batch, so a Save whose first
    // response was lost is replayed rather than recorded twice.
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.request(`${BY_ID_BASE}/bulk`, { method: 'POST', body: { entries }, headers });
  }

  /** The occurrences recorded against one line, newest first. */
  listForLine(
    customerExpenseId: string,
    opts: OccurrenceListQuery = {},
  ): Promise<Paged<CustomerVariableExpenseOccurrence>> {
    const query: Record<string, number> = {};
    if (opts.limit != null) query.limit = opts.limit;
    if (opts.offset != null) query.offset = opts.offset;
    return this.request(forLine(customerExpenseId), { query });
  }

  get(id: string): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`);
  }

  /** Record what a variable expense cost on one day. The line must point at a
   *  VARIABLE catalog item — a fixed line already carries its own price. */
  create(customerExpenseId: string, input: CreateOccurrenceInput): Promise<One> {
    return this.request(forLine(customerExpenseId), { method: 'POST', body: input });
  }

  patch(id: string, input: PatchOccurrenceInput): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /**
   * Hard delete. Unlike an expense LINE there is no "end it" alternative — an
   * occurrence either happened or it did not — so deleting one keyed in wrongly
   * is the correct move. An invoice already raised keeps its own copy of the
   * value and is unaffected.
   */
  delete(id: string): Promise<One> {
    return this.request(`${BY_ID_BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /**
   * Recorded amounts for the period, plus a blank row for each line that has
   * none.
   *
   * This is BOTH the export and the import's fill-in sheet — there is no
   * separate template endpoint. Download it, type into the blanks, upload it
   * back: rows that already carry an amount re-import as `unchanged`, and only
   * the blanks you filled become new occurrences.
   */
  exportXlsx(query: VariableExpenseGridQuery): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob(`${BY_ID_BASE}/export`, { query: gridQuery(query) });
  }

  /**
   * Upload a filled-in .xlsx or .csv.
   *
   * Every valid row is an INSERT — occurrences have no natural key, so there
   * is nothing to upsert against. Rows are matched to an expense LINE by Client
   * ID + expense item + the date, with the optional `Line` column settling the
   * rare case of one customer holding the same variable item twice at once.
   *
   * Pass `idempotencyKey` to make the request safely retryable — the server
   * stores the response for the key and replays it instead of re-applying.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart(`${BY_ID_BASE}/import`, fd, { headers });
  }
}
